import { Box, Group, Select, Table, Title } from "@mantine/core";
import Decimal from "decimal.js";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useQuery } from "urql";
import Loading from "../Loading";
import classes from "./ApiCosts.module.css";
import CostDetails from "./CostDetails";
import DailyCosts from "./DailyCosts";
import {
    type ApiCostFiltersQuery,
    type ApiCostFiltersQueryVariables,
    type ApiCostsMonthlyQuery,
    type ApiCostsMonthlyQueryVariables,
    apiCostFiltersQuery,
    apiCostsMonthlyQuery,
} from "./queries";
import { type DaySel, externalFilter, formatMonth, type MonthSel } from "./utils";

function ApiCosts(): JSX.Element {
    const [selectedModel, setSelectedModel] = useState<string | null>(null);
    const [selectedExternal, setSelectedExternal] = useState<string | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<MonthSel | null>(null);
    const [selectedDay, setSelectedDay] = useState<DaySel | null>(null);

    // Reset month/day when filters change to avoid stale selections
    // biome-ignore lint/correctness/useExhaustiveDependencies: Goal is to reset the month/day when the filters change
    useEffect(() => {
        setSelectedMonth(null);
        setSelectedDay(null);
    }, [selectedModel, selectedExternal]);

    const [filtersRes] = useQuery<ApiCostFiltersQuery, ApiCostFiltersQueryVariables>({ query: apiCostFiltersQuery });

    const { externalId, noExternal } = externalFilter(selectedExternal);

    const [monthlyRes] = useQuery<ApiCostsMonthlyQuery, ApiCostsMonthlyQueryVariables>({
        query: apiCostsMonthlyQuery,
        variables: { model: selectedModel || null, externalId, noExternal },
        requestPolicy: "cache-and-network",
    });

    const handleMonthSelect = (sel: MonthSel | null) => {
        setSelectedMonth(sel);
        setSelectedDay(null);
    };

    const handleDaySelect = (sel: DaySel) => {
        setSelectedDay((prev) => {
            if (prev && prev.year === sel.year && prev.month === sel.month && prev.day === sel.day) {
                return null;
            }
            return sel;
        });
    };

    const modelOptions = [
        { value: "", label: "All models" },
        ...(filtersRes.data?.modelList || []).map((model) => ({
            value: model,
            label: model,
        })),
    ];

    const externalOptions = [
        { value: "", label: "All" },
        { value: "none", label: "No external" },
        ...(filtersRes.data?.externalList || []).map((external) => ({
            value: String(external.id),
            label: external.name,
        })),
    ];

    let main = null;
    if (monthlyRes.fetching || filtersRes.fetching) {
        main = <Loading />;
    } else if (monthlyRes.error || filtersRes.error) {
        const err = monthlyRes.error || filtersRes.error;
        main = <Box>Error: {err?.message}</Box>;
    } else if (!monthlyRes.data?.apiCostsMonthly || monthlyRes.data.apiCostsMonthly.length === 0) {
        main = <Box>No API cost data available</Box>;
    } else {
        main = (
            <>
                <Group className={classes.filters}>
                    <Select
                        label="Filter by model"
                        placeholder="Select model"
                        data={modelOptions}
                        value={selectedModel}
                        onChange={setSelectedModel}
                        clearable
                        searchable
                    />
                    <Select
                        label="Filter by external"
                        placeholder="Select external"
                        data={externalOptions}
                        value={selectedExternal}
                        onChange={setSelectedExternal}
                        clearable
                        searchable
                    />
                </Group>

                <Title order={3} mt="md">
                    Monthly Costs
                </Title>
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Month</Table.Th>
                            <Table.Th>Cost (USD)</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {monthlyRes.data.apiCostsMonthly.map((monthCost) => {
                            const isSelected =
                                selectedMonth?.year === monthCost.year && selectedMonth?.month === monthCost.month;
                            return (
                                <Table.Tr
                                    key={`${monthCost.year}-${monthCost.month}`}
                                    className={`${classes.clickableRow} ${isSelected ? classes.selectedRow : ""}`}
                                    onClick={() =>
                                        handleMonthSelect(
                                            isSelected ? null : { year: monthCost.year, month: monthCost.month },
                                        )
                                    }
                                >
                                    <Table.Td>{formatMonth(monthCost.year, monthCost.month)}</Table.Td>
                                    <Table.Td>${new Decimal(monthCost.cost).toFixed(6)}</Table.Td>
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>

                {selectedMonth && (
                    <DailyCosts
                        selectedModel={selectedModel}
                        selectedExternal={selectedExternal}
                        selectedMonth={selectedMonth}
                        selectedDay={selectedDay}
                        onDaySelect={handleDaySelect}
                    />
                )}

                {selectedMonth && selectedDay ? (
                    <CostDetails
                        selectedModel={selectedModel}
                        selectedExternal={selectedExternal}
                        selectedMonth={selectedMonth}
                        selectedDay={selectedDay}
                    />
                ) : selectedMonth ? (
                    <Box mt="xl" p="md" ta="center" color="dimmed">
                        Select a day from the table above to view detailed costs.
                    </Box>
                ) : (
                    <Box mt="xl" p="md" ta="center" color="dimmed">
                        Select a month from the table above to view daily costs.
                    </Box>
                )}
            </>
        );
    }

    return (
        <Box className={classes.container}>
            <Title order={2}>API Costs</Title>
            {main}
        </Box>
    );
}

export default ApiCosts;
